import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, House, Question, ShoppingBag, SignOut, UserCircle } from "@phosphor-icons/react";
import { useLayoutTitle } from "../context/LayoutTitleContext.jsx";
import { useParticipateChrome } from "../context/ParticipateChromeContext.jsx";
import { useHeaderActions } from "../context/HeaderActionsContext.jsx";
import {
  clearWicoopSession,
  isVitrineLoggedIn,
} from "../lib/wicoopProfileStorage";
import { confirmTelegramAction } from "../lib/telegramWebAppAuth.js";
import styles from "./Layout.module.css";

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { participateHeaderLine } = useParticipateChrome();
  const { pageTitle } = useLayoutTitle();
  const { actions } = useHeaderActions();
  const isHome = pathname === "/";
  const purchasesActive = pathname === "/purchases";
  const [loggedIn, setLoggedIn] = useState(() => isVitrineLoggedIn());
  const profileTo = loggedIn ? "/profile" : "/profile/login";
  const profileActive = pathname.startsWith("/profile");
  const profileSubRoutes =
    pathname === "/profile" ||
    pathname === "/profile/settings" ||
    pathname === "/profile/active" ||
    pathname === "/profile/history";
  const showProfileLogout = profileSubRoutes && loggedIn;
  const [headerCollapsed, setHeaderCollapsed] = useState(false);

  useEffect(() => {
    setLoggedIn(isVitrineLoggedIn());
  }, [pathname]);

  useEffect(() => {
    const on = pathname.startsWith("/profile");
    document.body.classList.toggle("profileRoute", on);
    return () => document.body.classList.remove("profileRoute");
  }, [pathname]);

  useEffect(() => {
    const on = pathname === "/help";
    document.body.classList.toggle("helpRoute", on);
    return () => document.body.classList.remove("helpRoute");
  }, [pathname]);

  useEffect(() => {
    setHeaderCollapsed(window.scrollY > 12);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => {
      setHeaderCollapsed(window.scrollY > 12);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  async function handleProfileLogout() {
    const ok = await confirmTelegramAction(
      "Выйти? Данные в этом браузере будут очищены. Аккаунт на сервере сохранится — войдите снова по номеру телефона."
    );
    if (!ok) return;
    clearWicoopSession();
    setLoggedIn(false);
    navigate("/profile/login", { replace: true });
  }

  const showProfileBack =
    loggedIn &&
    (pathname === "/profile/settings" ||
      pathname === "/profile/active" ||
      pathname === "/profile/history");

  const headerCompact =
    !participateHeaderLine && headerCollapsed && (isHome || purchasesActive || !!pageTitle);
  const headerHiddenOnHomeTop =
    isHome && !headerCollapsed && !participateHeaderLine;
  const isParticipateRoute =
    pathname.includes("/giveaway/") && pathname.endsWith("/participate");

  return (
    <div className={styles.shell}>
      <header
        className={`${styles.header} ${headerCompact ? styles.headerCollapsed : ""} ${
          headerHiddenOnHomeTop ? styles.headerHomeHidden : ""
        }`}
      >
        <div className={styles.headerBar}>
          <Link to="/" className={styles.brand} aria-label="На главную">
            <img src="/logo.svg" alt="" className={styles.logo} width={36} height={36} />
          </Link>
          {participateHeaderLine ? (
            <p className={styles.headerProgress} aria-live="polite">
              {participateHeaderLine}
            </p>
          ) : (
            <>
              <div className={styles.headerTitleRow}>
                {showProfileBack ? (
                  <Link
                    to="/profile"
                    className={styles.headerBackLink}
                    aria-label="Назад к профилю"
                  >
                    <ArrowLeft size={22} weight="regular" aria-hidden />
                  </Link>
                ) : null}
                {pageTitle ? <h1 className={styles.headerTitle}>{pageTitle}</h1> : null}
              </div>
              <div className={styles.headerActions}>
                {actions}
                {showProfileLogout ? (
                  <button
                    type="button"
                    className={styles.headerLogout}
                    onClick={handleProfileLogout}
                    aria-label="Выйти из аккаунта"
                  >
                    <SignOut className={styles.headerLogoutIcon} size={20} weight="regular" />
                  </button>
                ) : null}
              </div>
            </>
          )}
        </div>
      </header>
      <main
        className={`${styles.main} ${pathname === "/help" ? styles.mainHelp : ""} ${
          pathname === "/purchases" ? styles.mainPurchases : ""
        } ${pathname === "/profile/login" ? styles.mainProfileLogin : ""} ${
          isParticipateRoute ? styles.mainParticipate : ""
        }`}
      >
        {children}
      </main>

      <nav className={styles.bottomNav} aria-label="Основное меню">
        <div className={styles.bottomNavInner}>
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.navItemActive : ""}`
            }
          >
            {({ isActive }) => (
              <span className={styles.navItemInner}>
                <House
                  className={styles.navIcon}
                  size={22}
                  weight={isActive ? "fill" : "regular"}
                  aria-hidden
                />
                <span className={styles.navLabel}>Главная</span>
              </span>
            )}
          </NavLink>
          <NavLink
            to="/purchases"
            className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.navItemActive : ""}`
            }
          >
            {({ isActive }) => (
              <span className={styles.navItemInner}>
                <ShoppingBag
                  className={styles.navIcon}
                  size={22}
                  weight={isActive ? "fill" : "regular"}
                  aria-hidden
                />
                <span className={styles.navLabel}>Покупки</span>
              </span>
            )}
          </NavLink>
          <NavLink
            to="/help"
            className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.navItemActive : ""}`
            }
          >
            {({ isActive }) => (
              <span className={styles.navItemInner}>
                <Question
                  className={styles.navIcon}
                  size={22}
                  weight={isActive ? "fill" : "regular"}
                  aria-hidden
                />
                <span className={styles.navLabel}>Помощь</span>
              </span>
            )}
          </NavLink>
          <NavLink
            to={profileTo}
            className={() =>
              `${styles.navItem} ${profileActive ? styles.navItemActive : ""}`
            }
          >
            {() => (
              <span className={styles.navItemInner}>
                <UserCircle
                  className={styles.navIcon}
                  size={22}
                  weight={profileActive ? "fill" : "regular"}
                  aria-hidden
                />
                <span className={styles.navLabel}>Профиль</span>
              </span>
            )}
          </NavLink>
        </div>
      </nav>
    </div>
  );
}
